import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, CheckCircle, Shield } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import FeedbackModal, { hasFeedback } from "../components/FeedbackModal";

export default function Kyc() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [step, setStep] = useState(1); // 1 = input details, 2 = OTP
  const [clientId, setClientId] = useState(""); // Surepass client_id from step 1
  const [form, setForm] = useState({
    aadhaar: "",
    pan: "",
    otp: "",
  });

  const isBroker = user?.role === "broker_admin";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Format Aadhaar as XXXX XXXX XXXX
  const formatAadhaar = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 12);
    return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
  };

  const handleAadhaar = (e) => {
    setForm({ ...form, aadhaar: formatAadhaar(e.target.value) });
  };

  const requestOtp = async () => {
    const aadhaarDigits = form.aadhaar.replace(/\s/g, "");
    if (aadhaarDigits.length !== 12) return toast.error("Enter a valid 12-digit Aadhaar number.");
    if (isBroker && form.pan.length !== 10) return toast.error("Enter a valid 10-character PAN number.");
    setBusy(true);
    try {
      const { data } = await api.post("/kyc/request-otp", {
        aadhaar: aadhaarDigits,
        pan: isBroker ? form.pan.toUpperCase() : undefined,
      });
      if (data.client_id) setClientId(data.client_id);
      toast.success(
        data.hint
          ? `OTP sent. ${data.hint}`
          : "OTP sent to your Aadhaar-linked mobile number."
      );
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async () => {
    if (form.otp.length !== 6) return toast.error("Enter the 6-digit OTP.");
    setBusy(true);
    try {
      await api.post("/kyc/verify-otp", {
        aadhaar:   form.aadhaar.replace(/\s/g, ""),
        pan:       isBroker ? form.pan.toUpperCase() : undefined,
        otp:       form.otp,
        client_id: clientId || undefined,
      });
      await refresh();
      setDone(true);
      if (!hasFeedback("onboarding", null)) setShowFeedback(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <header className="px-6 py-5 max-w-7xl mx-auto w-full"><Logo size="sm" /></header>
        <div className="flex-1 flex items-center justify-center px-4 pb-10">
          <div className="w-full max-w-lg card-base p-8 text-center" data-testid="kyc-done">
            <div className="h-16 w-16 mx-auto rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CheckCircle className="h-8 w-8" />
            </div>
            <h1 className="mt-5 text-2xl font-bold font-display">You're verified!</h1>
            <p className="text-sm text-gray-500 mt-1">Identity check complete. You're all set.</p>
            <button onClick={() => nav("/app")} className="mt-6 bg-[#F97316] hover:bg-[#EA580C] btn-press text-white rounded-lg px-6 py-2.5 text-sm font-medium" data-testid="go-dashboard">
              Go to Dashboard
            </button>
          </div>
        </div>
        {showFeedback && (
          <FeedbackModal
            type="onboarding"
            onClose={() => { setShowFeedback(false); nav("/app"); }}
            onSubmitted={() => { setShowFeedback(false); nav("/app"); }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="px-6 py-5 max-w-7xl mx-auto w-full"><Logo size="sm" /></header>
      <div className="flex-1 flex items-center justify-center px-4 pb-10">
        <div className="w-full max-w-lg card-base p-8" data-testid="kyc-page">

          <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-widest">
            <Shield className="h-3.5 w-3.5" /> Identity Verification
          </div>
          <h1 className="text-2xl font-bold font-display mt-1">Verify your identity</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isBroker
              ? "Aadhaar + PAN verification required for brokers."
              : "Aadhaar verification required to access all features."}
          </p>

          <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4 mt-2">
            <Shield className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-xs text-emerald-800">
              Verified via <strong>Surepass</strong> — India's trusted Aadhaar OTP authentication. Your data is encrypted end-to-end.
            </p>
          </div>

          {step === 1 && (
            <div className="mt-6 space-y-4">
              {/* Aadhaar input */}
              <div>
                <label className="text-sm font-medium">Aadhaar Number</label>
                <input
                  data-testid="kyc-aadhaar"
                  value={form.aadhaar}
                  onChange={handleAadhaar}
                  placeholder="XXXX XXXX XXXX"
                  maxLength={14}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">OTP will be sent to your Aadhaar-linked mobile number.</p>
              </div>

              {/* PAN input — broker admin only */}
              {isBroker && (
                <div>
                  <label className="text-sm font-medium">PAN Number</label>
                  <input
                    data-testid="kyc-pan"
                    value={form.pan}
                    onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                  />
                </div>
              )}

              <div className="text-xs text-gray-500 flex items-start gap-2 pt-1">
                <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                Your details are encrypted and used only for identity verification. We never share them.
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  data-testid="kyc-skip"
                  onClick={() => nav("/app")}
                  className="flex-1 border border-gray-300 rounded-lg py-2.5 text-sm font-medium text-gray-600"
                >
                  Skip for now
                </button>
                <button
                  data-testid="kyc-send-otp"
                  disabled={busy}
                  onClick={requestOtp}
                  className="flex-1 bg-[#F97316] hover:bg-[#EA580C] btn-press text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-50"
                >
                  {busy ? "Sending…" : "Send OTP"}
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mt-6 space-y-4">
              <div>
                <label className="text-sm font-medium">Enter OTP</label>
                <p className="text-xs text-gray-500 mt-0.5">Sent to your Aadhaar-linked mobile number.</p>
                <input
                  data-testid="kyc-otp"
                  value={form.otp}
                  onChange={set("otp")}
                  placeholder="6-digit OTP"
                  maxLength={6}
                  className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                />
              </div>

              <button
                onClick={() => setStep(1)}
                className="text-sm text-[#F97316] hover:underline"
              >
                ← Change details
              </button>

              <div className="flex gap-3 pt-2">
                <button
                  data-testid="kyc-skip"
                  onClick={() => nav("/app")}
                  className="flex-1 border border-gray-300 rounded-lg py-2.5 text-sm font-medium text-gray-600"
                >
                  Skip for now
                </button>
                <button
                  data-testid="kyc-verify"
                  disabled={busy}
                  onClick={submitOtp}
                  className="flex-1 bg-[#F97316] hover:bg-[#EA580C] btn-press text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-50"
                >
                  {busy ? "Verifying…" : "Verify"}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}